import * as React from 'react';

import { IconCross, IconTick, IconPlus } from '@xing-com/icons';

import { TagContent } from './tag-content';
import * as Styled from './tag.styles';
import type { AddTagProps } from './tag.types';

export const AddTag = React.forwardRef<HTMLDivElement, AddTagProps>(
  (
    {
      children,
      className,
      disabled,
      fontWeight,
      maxWidth = '330px',
      onClick,
      profile,
      selected,
      size = 'medium',
      value,
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const handleOnClick = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): void => {
      if (!disabled && onClick) {
        event.currentTarget.dataset.value = value;
        onClick(event);
      }
    };

    const [hovered, setHovered] = React.useState(false);
    const [touched, setTouched] = React.useState(false);

    return (
      <Styled.InteractiveTag
        $disabled={disabled}
        $fontWeight={fontWeight}
        $profile={Boolean(profile)}
        $selected={selected}
        $size={size}
        aria-disabled={disabled}
        aria-pressed={selected}
        as="button"
        className={className}
        data-xds="AddTag"
        disabled={disabled}
        onClick={handleOnClick}
        onPointerOut={() => setHovered(false)}
        onPointerOver={() => setHovered(true)}
        onTouchEnd={() => setTouched(false)}
        onTouchStart={() => setTouched(true)}
        ref={forwardedRef}
        role="button"
        type="button"
        value={value}
        {...props}
      >
        <TagContent
          badgeProps={undefined}
          disabled={disabled}
          leadingIcon={
            selected ? (
              (hovered || touched) && onClick && !disabled ? (
                <IconCross
                  aria-hidden="true"
                  data-qa="icon-shape-cross"
                  data-testid="icon-shape-cross"
                />
              ) : (
                <IconTick
                  aria-hidden="true"
                  data-qa="icon-shape-tick"
                  data-testid="icon-shape-tick"
                />
              )
            ) : (
              <IconPlus
                aria-hidden="true"
                data-qa="icon-shape-plus"
                data-testid="icon-shape-plus"
              />
            )
          }
          maxWidth={maxWidth}
          profile={profile}
          selected={selected}
          size={size}
          trailingIcon={null}
        >
          {children}
        </TagContent>
      </Styled.InteractiveTag>
    );
  }
);

AddTag.displayName = 'AddTag';
